import {useCallback, useEffect, useRef, useState} from 'react'

import {Point, ViewBoxRect} from '../types'

const PAN_RENDER_THRESHOLD = 0.001

type UseDrawingPanArgs = {
  svg: SVGSVGElement | null
  viewBoxRect: ViewBoxRect
  setViewBoxRect: React.Dispatch<React.SetStateAction<ViewBoxRect>>
  activePointerIdRef: React.MutableRefObject<number | null>
  isPointerCapturedRef: React.MutableRefObject<boolean>
  shouldSuppressClickRef: React.MutableRefObject<boolean>
}

export const useDrawingPan = ({
  svg,
  viewBoxRect,
  setViewBoxRect,
  activePointerIdRef,
  isPointerCapturedRef,
  shouldSuppressClickRef
}: UseDrawingPanArgs) => {
  const [isPanning, setIsPanning] = useState(false)
  const panStartClientPointRef = useRef<Point | null>(null)
  const panStartViewBoxRef = useRef<ViewBoxRect | null>(null)
  const animationFrameIdRef = useRef<number | null>(null)
  const pendingViewBoxRef = useRef<ViewBoxRect | null>(null)

  useEffect(() => {
    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current)
        animationFrameIdRef.current = null
      }
    }
  }, [])

  const handlePanStart = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      activePointerIdRef.current = e.pointerId
      isPointerCapturedRef.current = false
      setIsPanning(true)
      panStartClientPointRef.current = {x: e.clientX, y: e.clientY}
      panStartViewBoxRef.current = viewBoxRect
      shouldSuppressClickRef.current = true

      e.currentTarget.setPointerCapture(e.pointerId)
      isPointerCapturedRef.current = true
    },
    [activePointerIdRef, isPointerCapturedRef, shouldSuppressClickRef, viewBoxRect]
  )

  const handlePanMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const panStartClientPoint = panStartClientPointRef.current
      const panStartViewBox = panStartViewBoxRef.current

      if (!panStartClientPoint || !panStartViewBox || !svg) return

      const bounds = svg.getBoundingClientRect()
      if (bounds.width <= 0 || bounds.height <= 0) return

      const unitsPerPixelX = panStartViewBox.width / bounds.width
      const unitsPerPixelY = panStartViewBox.height / bounds.height

      const dx = (e.clientX - panStartClientPoint.x) * unitsPerPixelX
      const dy = (e.clientY - panStartClientPoint.y) * unitsPerPixelY

      const nextPanViewBox: ViewBoxRect = {
        ...panStartViewBox,
        x: panStartViewBox.x - dx,
        y: panStartViewBox.y - dy
      }

      const pendingViewBox = pendingViewBoxRef.current
      if (
        pendingViewBox &&
        Math.abs(nextPanViewBox.x - pendingViewBox.x) < PAN_RENDER_THRESHOLD &&
        Math.abs(nextPanViewBox.y - pendingViewBox.y) < PAN_RENDER_THRESHOLD
      ) {
        return
      }

      pendingViewBoxRef.current = nextPanViewBox

      if (animationFrameIdRef.current === null) {
        animationFrameIdRef.current = requestAnimationFrame(() => {
          animationFrameIdRef.current = null
          const nextViewBox = pendingViewBoxRef.current
          if (!nextViewBox) return

          setViewBoxRect((prevViewBox) => {
            if (
              Math.abs(nextViewBox.x - prevViewBox.x) < PAN_RENDER_THRESHOLD &&
              Math.abs(nextViewBox.y - prevViewBox.y) < PAN_RENDER_THRESHOLD
            ) {
              return prevViewBox
            }

            return nextViewBox
          })
        })
      }
    },
    [setViewBoxRect, svg]
  )

  const resetPanState = useCallback(() => {
    setIsPanning(false)
    panStartClientPointRef.current = null
    panStartViewBoxRef.current = null
    pendingViewBoxRef.current = null

    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current)
      animationFrameIdRef.current = null
    }
  }, [])

  const handlePanEnd = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      resetPanState()

      if (isPointerCapturedRef.current && activePointerIdRef.current !== null) {
        e.currentTarget.releasePointerCapture(activePointerIdRef.current)
      }
      isPointerCapturedRef.current = false
      activePointerIdRef.current = null

      requestAnimationFrame(() => {
        shouldSuppressClickRef.current = false
      })
    },
    [activePointerIdRef, isPointerCapturedRef, resetPanState, shouldSuppressClickRef]
  )

  const handlePanCancel = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      resetPanState()

      if (isPointerCapturedRef.current && activePointerIdRef.current !== null) {
        e.currentTarget.releasePointerCapture(activePointerIdRef.current)
      }
      isPointerCapturedRef.current = false
      activePointerIdRef.current = null
      shouldSuppressClickRef.current = false
    },
    [activePointerIdRef, isPointerCapturedRef, resetPanState, shouldSuppressClickRef]
  )

  return {
    isPanning,
    handlePanStart,
    handlePanMove,
    handlePanEnd,
    handlePanCancel
  }
}
